import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  ShieldAlert,
  Bell,
  Zap,
  Users,
  Siren,
  BadgeCheck,
  FileText,
  MapPin,
  Clock,
  AlertTriangle,
  Lock,
  HeartPulse,
} from "lucide-react";
import Logo from "../../components/site/Logo";
import Footer from "../../components/site/Footer";
import useSeo from "../../hooks/useSeo";

const FEATURES = [
  { icon: Bell, title: "Alarma sonora disuasoria", text: "Emite una alarma potente ante una situación de peligro para disuadir y atraer atención." },
  { icon: Zap, title: "Luz estroboscópica", text: "Señal luminosa de emergencia que ayuda a alertar y localizar a la víctima." },
  { icon: Users, title: "Aviso a contactos", text: "Notifica automáticamente a tus contactos de confianza con tu ubicación en tiempo real." },
  { icon: Siren, title: "Enlace con emergencias", text: "Conexión con los servicios de emergencia y registro cifrado del incidente." },
];

// Datos oficiales del justificante de la OEPM (modelo de utilidad)
const OEPM = [
  { label: "Nº de solicitud", value: "U202631775" },
  { label: "Título de la invención", value: "Sistema electrónico de protección personal" },
  { label: "Tipo de trámite", value: "Modelo de utilidad (ESUMNAT)" },
  { label: "Oficina receptora", value: "OEPM · España (Madrid)" },
  { label: "Fecha de recepción", value: "28/08/2026" },
  { label: "Solicitante", value: "Manuel Antonio Evangelista Piris" },
  { label: "Nacionalidad", value: "España (ES)" },
  { label: "Método de presentación", value: "Electrónico (en línea)" },
];

// Recreación del Informe de alerta AUXILIA (datos sensibles protegidos)
const ALERTA = [
  { label: "Tipo", value: "Agresión" },
  { label: "Estado", value: "Resuelta" },
  { label: "Modo", value: "Alarma" },
  { label: "Emergencias avisadas", value: "Sí" },
  { label: "Inicio", value: "29/08/2026 · 0:00:53" },
  { label: "Fin", value: "29/08/2026 · 0:01:16" },
  { label: "Duración", value: "23 segundos" },
  { label: "Referencia", value: "a261f544-…-661fa2c6beaa" },
];

const Protegido = () => (
  <span
    data-testid="dato-protegido"
    className="inline-flex items-center gap-1.5 rounded-sm bg-gray-900/90 text-white px-2 py-0.5 text-xs font-bold tracking-wider select-none"
    title="Dato protegido por privacidad"
  >
    <Lock size={12} /> •••• dato protegido
  </span>
);

export default function Auxilia() {
  useSeo({
    title: "AUXILIA E² — Sistema de protección personal registrado (OEPM)",
    description:
      "AUXILIA E² es un sistema electrónico de protección personal con solicitud registrada en la OEPM (Nº U202631775). Alarma, luz estroboscópica, aviso a contactos y enlace con emergencias.",
    path: "auxilia",
  });

  return (
    <div className="bg-[#FAFAFA] min-h-screen flex flex-col">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="auxilia-home-link">
            <Logo />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-bold text-espana-red hover:text-espana-reddark transition-colors"
          >
            <ArrowLeft size={16} /> Volver al inicio
          </Link>
        </div>
      </header>

      <main className="flex-1">
        {/* HERO */}
        <section className="bg-espana-red text-white">
          <div className="max-w-5xl mx-auto px-5 lg:px-8 py-16 lg:py-20">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center justify-center h-12 w-12 bg-white/15 mb-6">
                <ShieldAlert size={26} />
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-espana-gold mb-3">
                Sistemas E²
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none">
                AUXILIA E²
              </h1>
              <p className="mt-5 text-lg text-white/90 max-w-2xl leading-relaxed">
                Un sistema electrónico de <strong>protección personal</strong> pensado
                para actuar en segundos ante una agresión: alarma sonora, luz de
                emergencia y aviso automático a tus contactos y a los servicios de
                emergencia con tu ubicación.
              </p>
              <div className="mt-7 inline-flex items-center gap-2.5 bg-white text-espana-red px-4 py-2 text-sm font-bold">
                <BadgeCheck size={18} />
                Solicitud registrada en la OEPM · Nº U202631775
              </div>
            </motion.div>
          </div>
        </section>

        {/* CÓMO FUNCIONA */}
        <section className="max-w-5xl mx-auto px-5 lg:px-8 py-14 lg:py-16">
          <div className="h-1.5 w-24 bg-espana-red mb-6" />
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight">
            Cómo protege AUXILIA E²
          </h2>
          <p className="mt-3 text-gray-600 max-w-3xl">
            Cuatro respuestas simultáneas activadas con un solo gesto, diseñadas para
            disuadir al agresor y acelerar el auxilio.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 gap-5">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  data-testid={`auxilia-feature-${i}`}
                  className="bg-white border border-gray-200 p-6 hover:-translate-y-1 hover:shadow-md transition-transform"
                >
                  <div className="inline-flex items-center justify-center h-11 w-11 bg-espana-red text-white mb-4">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-bold text-lg">{f.title}</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">{f.text}</p>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* LEGITIMIDAD OEPM */}
        <section className="bg-white border-y border-gray-200">
          <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14 lg:py-16">
            <div className="flex items-start gap-4">
              <div className="hidden sm:inline-flex items-center justify-center h-12 w-12 bg-espana-gold text-espana-reddark shrink-0">
                <BadgeCheck size={26} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-espana-red mb-2">
                  Propiedad industrial
                </p>
                <h2 className="text-2xl sm:text-3xl font-black tracking-tight">
                  Solicitud registrada en la OEPM
                </h2>
                <p className="mt-3 text-gray-600 max-w-3xl leading-relaxed">
                  AUXILIA E² cuenta con una solicitud de <strong>modelo de utilidad</strong>{" "}
                  presentada electrónicamente ante la Oficina Española de Patentes y
                  Marcas (OEPM), organismo del Ministerio de Industria, Comercio y
                  Turismo. Estos son los datos oficiales del justificante de presentación.
                </p>
              </div>
            </div>

            <div
              data-testid="auxilia-oepm-card"
              className="mt-8 border border-gray-200 rounded-sm overflow-hidden"
            >
              <div className="flex items-center gap-2 bg-gray-900 text-white px-5 py-3">
                <FileText size={18} className="text-espana-gold" />
                <span className="font-bold text-sm tracking-wide">
                  Justificante de presentación · OEPM
                </span>
              </div>
              <dl className="grid sm:grid-cols-2">
                {OEPM.map((row, i) => (
                  <div
                    key={row.label}
                    className={`px-5 py-4 border-gray-200 ${
                      i % 2 === 0 ? "sm:border-r" : ""
                    } border-b`}
                  >
                    <dt className="text-xs font-bold uppercase tracking-wide text-gray-400">
                      {row.label}
                    </dt>
                    <dd className="mt-1 font-semibold text-gray-900">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <p className="mt-3 text-xs text-gray-400">
              Advertencia OEPM: los datos de una solicitud pueden publicarse en el
              Boletín Oficial de la Propiedad Industrial y en el Registro de Patentes,
              de acceso público.
            </p>
          </div>
        </section>

        {/* CASO REAL — INFORME DE ALERTA */}
        <section className="max-w-5xl mx-auto px-5 lg:px-8 py-14 lg:py-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-espana-red mb-2">
            Caso real
          </p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight">
            Informe de alerta generado por AUXILIA
          </h2>
          <p className="mt-3 text-gray-600 max-w-3xl leading-relaxed">
            Ejemplo real de un informe generado automáticamente tras una alerta. Por
            privacidad, los teléfonos de los contactos y los datos médicos aparecen{" "}
            <strong>protegidos</strong>.
          </p>

          <div
            data-testid="auxilia-informe-card"
            className="mt-8 bg-white border border-gray-200 rounded-sm overflow-hidden shadow-sm"
          >
            {/* cabecera informe */}
            <div className="flex items-center justify-between bg-espana-red text-white px-5 py-4">
              <div className="flex items-center gap-2.5">
                <AlertTriangle size={20} />
                <span className="font-black tracking-wide">AUXILIA · Informe de alerta</span>
              </div>
              <span className="text-xs bg-white/15 px-2.5 py-1 font-bold">Resuelta</span>
            </div>

            {/* datos de la alerta */}
            <div className="px-5 py-5 border-b border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Clock size={16} className="text-espana-red" />
                <h3 className="font-bold">Datos de la alerta</h3>
              </div>
              <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {ALERTA.map((row) => (
                  <div key={row.label} className="flex justify-between gap-4 border-b border-dashed border-gray-100 pb-2">
                    <dt className="text-sm text-gray-500">{row.label}</dt>
                    <dd className="text-sm font-semibold text-gray-900 text-right">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* datos médicos */}
            <div className="px-5 py-5 border-b border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <HeartPulse size={16} className="text-espana-red" />
                <h3 className="font-bold">Datos médicos</h3>
              </div>
              <dl className="space-y-3">
                <div className="flex justify-between gap-4">
                  <dt className="text-sm text-gray-500">Grupo sanguíneo</dt>
                  <dd className="text-sm font-semibold text-gray-900">No indicado</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-sm text-gray-500">Alergias</dt>
                  <dd className="text-sm font-semibold text-gray-900">Ninguna conocida</dd>
                </div>
                <div className="flex justify-between gap-4 items-center">
                  <dt className="text-sm text-gray-500">Medicación</dt>
                  <dd className="text-right"><Protegido /></dd>
                </div>
              </dl>
            </div>

            {/* contactos notificados */}
            <div className="px-5 py-5 border-b border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Users size={16} className="text-espana-red" />
                <h3 className="font-bold">Contactos notificados</h3>
              </div>
              <ul className="space-y-3">
                {["Contacto 1", "Contacto 2"].map((c) => (
                  <li key={c} className="flex justify-between items-center gap-4">
                    <span className="text-sm font-semibold text-gray-900">{c}</span>
                    <Protegido />
                  </li>
                ))}
              </ul>
            </div>

            {/* ubicaciones */}
            <div className="px-5 py-5">
              <div className="flex items-center gap-2 mb-4">
                <MapPin size={16} className="text-espana-red" />
                <h3 className="font-bold">Ubicaciones registradas (2)</h3>
              </div>
              <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                <div className="flex justify-between gap-4">
                  <dt className="text-sm text-gray-500">Inicio</dt>
                  <dd className="text-sm font-semibold text-gray-900">42.318219, -7.882230</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-sm text-gray-500">Última</dt>
                  <dd className="text-sm font-semibold text-gray-900">42.318308, -7.882314</dd>
                </div>
              </dl>
            </div>

            <div className="bg-gray-50 px-5 py-3 text-xs text-gray-400 border-t border-gray-200">
              Informe generado por AUXILIA el 29/08/2026, 0:01:24. La grabación
              audiovisual, si existe, se conserva en el registro cifrado asociado a la
              referencia de esta alerta.
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gray-900 text-white">
          <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14 text-center">
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight">
              ¿Quieres saber más sobre AUXILIA E²?
            </h2>
            <p className="mt-3 text-white/80 max-w-2xl mx-auto">
              Escríbenos para conocer el proyecto, colaborar o solicitar información
              sobre el sistema de protección personal.
            </p>
            <Link
              to="/#contacto"
              data-testid="auxilia-cta"
              className="mt-7 inline-flex items-center gap-2 bg-espana-red hover:bg-espana-reddark font-bold px-7 py-3.5 rounded-sm transition-colors"
            >
              Solicitar información <ArrowRight size={18} />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
